import type { TeamInput } from '@airmail/engine';
import { parseTable, type ParsedTable } from './table.js';

/** Zieltypen je Spalte (docs/05 §3) */
export type ColumnRole =
  | 'teamName'
  | 'player1'
  | 'player2'
  | 'player3'
  | 'player4'
  | 'player' // Layout B: eine Zeile pro Spieler
  | 'players' // Layout C: "Roth / Müller" in einer Zelle
  | 'firstName' // Vorname und Nachname in getrennten Spalten, eine Zeile je Spieler
  | 'lastName'
  | 'club'
  | 'email'
  | 'phone'
  | 'seed'
  | 'competition'
  | 'paid'
  | 'note'
  | 'ignore';

export const ROLE_LABELS: Record<ColumnRole, string> = {
  teamName: 'Teamname',
  player1: 'Spieler 1',
  player2: 'Spieler 2',
  player3: 'Spieler 3',
  player4: 'Spieler 4',
  player: 'Spieler (eine Zeile je Spieler)',
  players: 'Spieler (alle in einer Zelle)',
  firstName: 'Vorname',
  lastName: 'Nachname',
  club: 'Verein / Ort',
  email: 'E-Mail',
  phone: 'Telefon',
  seed: 'Setzung',
  competition: 'Bewerb',
  paid: 'Bezahlt',
  note: 'Notiz',
  ignore: 'ignorieren',
};

export type Mapping = ColumnRole[];

const HEADER_RULES: [RegExp, ColumnRole][] = [
  [/^(spieler|player)\s*1$|^partner\s*1$|^vorname\s*1$|^name\s*1$/i, 'player1'],
  [/^(spieler|player)\s*2$|^partner(\s*2)?$|^name\s*2$/i, 'player2'],
  [/^(spieler|player)\s*3$/i, 'player3'],
  [/^(spieler|player)\s*4$/i, 'player4'],
  /*
   * Vorname/Nachname getrennt (Harald, 10/2026: „Nome | Cognome" — die Kopfzeile
   * wurde nicht erkannt, „Nome" landete als Teamname, und drei „Marco" fielen zu
   * einem Team zusammen). Vor den allgemeinen Regeln, damit „Name" allein weiter
   * „Spieler" bleibt, „Nome" aber Vorname ist.
   */
  [/^(vorname|nome|first ?name|given ?name|pr[ée]nom)$/i, 'firstName'],
  [/^(nachname|cognome|last ?name|surname|family ?name|familienname|nom de famille)$/i, 'lastName'],
  [/^(team|teamname|mannschaft|team ?name|squadra|nome ?squadra)$/i, 'teamName'],
  [/^(spieler|player|teilnehmer|participant)$/i, 'player'],
  [/^(spieler|players|namen|names)$/i, 'players'],
  [/^(verein|club|ort|city|stadt|gemeinde|comune|paese|municipality|societ[aà]|verein ?\/ ?ort)$/i, 'club'],
  [/^(e-?mail|mail)$/i, 'email'],
  [/^(telefon|tel|phone|handy|mobil|telefono|cellulare|contatto|kontakt|contact)$/i, 'phone'],
  [/^(setzung|seed|rang|rank|ranking)$/i, 'seed'],
  [/^(bewerb|division|kategorie|category|klasse)$/i, 'competition'],
  [/^(bezahlt|paid|startgeld|gezahlt)$/i, 'paid'],
  [/^(notiz|note|bemerkung|kommentar|comment)$/i, 'note'],
];

const SPLIT_NAMES = /\s*(?:\/|&|\+|,|\bund\b|\band\b)\s*/i;

function looksLikeTwoNames(cell: string): boolean {
  const parts = cell.split(SPLIT_NAMES).filter(Boolean);
  return parts.length === 2 && parts.every((p) => /^[\p{L}][\p{L}'.\- ]*$/u.test(p));
}

/** Auto-Mapping aus Überschriften, sonst aus Inhalten */
export function suggestMapping(table: ParsedTable): Mapping {
  const width = table.header.length;
  const mapping: Mapping = Array.from({ length: width }, () => 'ignore');
  const used = new Set<ColumnRole>();
  const take = (i: number, role: ColumnRole): void => {
    if (used.has(role) && role !== 'ignore') return;
    mapping[i] = role;
    used.add(role);
  };

  if (table.hasHeader) {
    table.header.forEach((h, i) => {
      for (const [re, role] of HEADER_RULES) {
        if (re.test(h.trim())) {
          take(i, role);
          return;
        }
      }
    });
  }

  // „Team: Spieler; Spieler" — die erste Spalte ist der Name, egal wie sie aussieht
  if (table.colonForm && mapping[0] === 'ignore' && !used.has('teamName')) take(0, 'teamName');

  // Inhalte prüfen für noch nicht zugeordnete Spalten
  table.header.forEach((_, i) => {
    if (mapping[i] !== 'ignore') return;
    const col = table.rows.map((r) => r[i] ?? '').filter((c) => c !== '');
    if (col.length === 0) return;
    const share = (pred: (c: string) => boolean): number => col.filter(pred).length / col.length;
    if (share((c) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(c)) > 0.6) return take(i, 'email');
    if (share((c) => /^\+?[\d\s/()-]{6,}$/.test(c)) > 0.6) return take(i, 'phone');
    if (share((c) => /^(ja|nein|x|✓|yes|no|y|n|true|false|1|0)$/i.test(c)) > 0.8 && col.length > 1 && !col.every((c) => /^\d+$/.test(c))) return take(i, 'paid');
    if (share((c) => /^\d{1,3}$/.test(c)) > 0.8 && new Set(col).size === col.length) return take(i, 'seed');
    if (share(looksLikeTwoNames) > 0.6) return take(i, 'players');
    if (share((c) => /^[\p{L}][\p{L}'.-]+(\s+[\p{L}][\p{L}'.-]+)+$/u.test(c)) > 0.7) {
      if (!used.has('player1')) return take(i, 'player1');
      if (!used.has('player2')) return take(i, 'player2');
    }
    if (!used.has('teamName') && share((c) => /\p{L}/u.test(c)) > 0.8) return take(i, 'teamName');
    /*
     * Eine Textspalte, in der sich die Werte häufen („Ceva", „Ceva", „Nucetto" …),
     * ist die Mannschaft bzw. der Verein — Spielernamen wiederholen sich nicht.
     * Harald, 09/2026: „Team, Spieler 1, Spieler 2, Mannschaft" ohne Überschrift.
     */
    if (used.has('teamName') && !used.has('club') && col.length >= 4 && new Set(col.map((c) => c.toLowerCase())).size <= col.length / 2 && share((c) => /^[\p{L}][\p{L}\d'.\- ]*$/u.test(c)) > 0.8)
      return take(i, 'club');
    /*
     * Weitere Textspalten nach dem Teamnamen sind Spieler — auch bei bloßen
     * Vornamen („Daniel"), bei „Müller, Hans" und auch, wenn Ziffern darin
     * vorkommen. Die Ziffern kosteten Harald drei Mannschaften: Seine
     * Platzhalter hießen „Pers C1", daran scheiterte die Erkennung, die Spalte
     * fiel auf „ignorieren" und jede Zeile stand ohne Spieler da.
     *
     * Was eindeutig etwas anderes ist — E-Mail, Telefon, Setzung, bezahlt —
     * wurde oben schon abgefangen; erst danach greift diese Regel. „Auf
     * ignorieren" bleibt also für das, was wirklich unklar ist.
     */
    if (used.has('teamName') && share((c) => /^[\p{L}][\p{L}\d',.\- ]*$/u.test(c)) > 0.6) {
      for (const role of ['player1', 'player2', 'player3', 'player4'] as const) if (!used.has(role)) return take(i, role);
    }
  });

  // Nur ein Spieler ohne Teamname und ohne zweiten → Layout B wahrscheinlich, wenn Teamname-Spalte doppelte Werte hat
  const tn = mapping.indexOf('teamName');
  if (tn >= 0 && mapping.includes('player1') && !mapping.includes('player2')) {
    const names = table.rows.map((r) => r[tn] ?? '');
    if (new Set(names).size < names.length) mapping[mapping.indexOf('player1')] = 'player';
  }
  return mapping;
}

// ---------------------------------------------------------------------------
// Zeilen → Teams
// ---------------------------------------------------------------------------

export interface ImportIssue {
  row: number;
  level: 'error' | 'warning';
  message: string;
}

export interface ImportTeam {
  /** Quellzeilen (0-basiert, in table.rows) */
  rows: number[];
  team: TeamInput;
  email?: string;
  phone?: string;
  competition?: string;
  issues: ImportIssue[];
}

export interface ImportResult {
  teams: ImportTeam[];
  issues: ImportIssue[];
  /** Summen für die Vorschau */
  summary: { total: number; valid: number; withWarnings: number; withErrors: number; waitlist: number };
}

export interface ImportOptions {
  teamSize: number;
  /** Bestehende Teamnamen (für Duplikat-Warnung) */
  existingNames?: string[];
  /** Teilnehmerlimit — überzählige gehen auf die Warteliste */
  limit?: number;
}

export function splitPersonName(full: string): { firstName: string; lastName: string } {
  const clean = titleCase(full.replace(/\s+/g, ' ').trim());
  if (clean.includes(',')) {
    const [last, first] = clean.split(',').map((s) => s.trim());
    return { firstName: first ?? '', lastName: last ?? '' };
  }
  const parts = clean.split(' ');
  if (parts.length === 1) return { firstName: '', lastName: parts[0] as string };
  return { firstName: parts.slice(0, -1).join(' '), lastName: parts[parts.length - 1] as string };
}

/** GROSSBUCHSTABEN → Titelschreibung; gemischte Schreibweisen bleiben */
export function titleCase(s: string): string {
  if (s !== s.toUpperCase() || !/\p{L}/u.test(s)) return s;
  return s.toLowerCase().replace(/(^|[\s\-'/])(\p{L})/gu, (_m, pre: string, ch: string) => pre + ch.toUpperCase());
}

function parseBool(s: string): boolean | undefined {
  if (/^(ja|x|✓|yes|y|true|1|bezahlt|paid)$/i.test(s)) return true;
  if (/^(nein|no|n|false|0|offen|open)$/i.test(s) || s === '') return false;
  return undefined;
}

export function rowsToTeams(table: ParsedTable, mapping: Mapping, opts: ImportOptions): ImportResult {
  const col = (role: ColumnRole): number => mapping.indexOf(role);
  const cell = (row: string[], role: ColumnRole): string => {
    const i = col(role);
    return i >= 0 ? (row[i] ?? '').trim() : '';
  };
  const teams: ImportTeam[] = [];
  const globalIssues: ImportIssue[] = [];

  const getrennt = col('firstName') >= 0 || col('lastName') >= 0;
  /** Die Person einer Zeile — aus „Spieler" oder aus Vorname + Nachname */
  const personVon = (r: string[]): { firstName: string; lastName: string } => {
    if (getrennt) return { firstName: titleCase(cell(r, 'firstName')), lastName: titleCase(cell(r, 'lastName')) };
    return splitPersonName(cell(r, 'player'));
  };

  if (col('player') >= 0 || getrennt) {
    /*
     * Layout B: eine Zeile je Spieler. Bei Einzelturnieren ist jede Zeile ein
     * Teilnehmer — **nie gruppieren**: Harald hatte drei „Marco" untereinander,
     * und die Gruppierung über die erste Spalte machte daraus *ein* Team mit
     * drei Spielern. Bei Doppeln gruppiert der Teamname, aber nie über die
     * Teamgröße hinaus; was darüber liegt, wird ein eigenes Team mit Warnung.
     */
    const einzel = opts.teamSize <= 1;
    const groups = new Map<string, number[]>();
    table.rows.forEach((row, ri) => {
      const key = einzel ? `__zeile${ri}` : cell(row, 'teamName') || `__block${Math.floor(ri / opts.teamSize)}`;
      groups.set(key, [...(groups.get(key) ?? []), ri]);
    });
    for (const [key, alleZeilen] of groups) {
      // Zu viele Zeilen mit demselben Teamnamen → in Teamgröße aufteilen statt zusammenwerfen
      const stuecke: number[][] = [];
      for (let k = 0; k < alleZeilen.length; k += Math.max(1, opts.teamSize)) stuecke.push(alleZeilen.slice(k, k + Math.max(1, opts.teamSize)));
      for (const rows of stuecke) {
        const first = table.rows[rows[0] as number] as string[];
        const players = rows.map((ri) => {
          const r = table.rows[ri] as string[];
          const club = cell(r, 'club');
          let person = personVon(r);
          /*
           * Zwei Spalten mit je einem Wort ohne Kopfzeile („Marco | Blangero"):
           * Die erste hält die Erkennung für den Teamnamen, sie ist aber der
           * Vorname. Bei Einzelspielern gehört sie zur Person.
           */
          if (einzel && !getrennt && !person.firstName && cell(r, 'teamName') && !/\s/.test(cell(r, 'teamName'))) person = { firstName: titleCase(cell(r, 'teamName')), lastName: person.lastName };
          return { ...person, ...(club ? { club } : {}) };
        });
        let name = key.startsWith('__') ? '' : key;
        // Einzel: Ein Teamname, der nur der Vorname war, ist keiner
        if (einzel && name && players.length === 1 && name.toLowerCase() === (players[0]?.firstName ?? '').toLowerCase()) name = '';
        const team = buildTeam(rows, name, players, first, cell, opts);
        if (stuecke.length > 1)
          team.issues.push({ row: rows[0] as number, level: 'warning', message: `${alleZeilen.length} Zeilen mit „${key}" — mehr als ${opts.teamSize} Spieler je Team, deshalb getrennt übernommen` });
        teams.push(team);
      }
    }
  } else {
    table.rows.forEach((row, ri) => {
      let players: { firstName: string; lastName: string; club?: string }[] = [];
      const multi = cell(row, 'players');
      if (multi) players = multi.split(SPLIT_NAMES).filter(Boolean).map(splitPersonName);
      for (const role of ['player1', 'player2', 'player3', 'player4'] as const) {
        const v = cell(row, role);
        if (v) players.push(splitPersonName(v));
      }
      let name = cell(row, 'teamName');
      // Zeile ohne Trennzeichen wie „Peter / Harald" in der Teamspalte: das sind die Spieler, kein Teamname
      if (players.length === 0 && name && looksLikeTwoNames(name)) {
        players = name.split(SPLIT_NAMES).filter(Boolean).map(splitPersonName);
        name = '';
      }
      teams.push(buildTeam([ri], name, players, row, cell, opts));
    });
    // Gleicher Teamname in mehreren Zeilen mit je einem Spieler („test2; Vera" + „test2; Carlo") → ein Team
    for (let i = teams.length - 1; i > 0; i--) {
      const cur = teams[i] as ImportTeam;
      const key = (cur.team.name ?? '').toLowerCase();
      if (!key) continue;
      /*
       * Eine Zeile, die noch **Inhalt** mitbringt, darf hier nie verschwinden.
       * Genau das ist Harald passiert: In seiner Datei hießen vier Zeilen
       * gleich („Mombarcaro1"), und weil die Spielerspalten nicht erkannt
       * waren, hatte jede null Spieler — diese Schleife schluckte drei davon
       * samt ihrer Namen, ohne ein Wort zu sagen. Doppelte Namen gehören in
       * die Duplikat-Behandlung unten, die umbenennt und warnt.
       *
       * Eine Zeile wie „test2; " dagegen — Name wiederholt, sonst nichts —
       * trägt nichts bei; die darf weiter zusammenfallen.
       */
      const rohzeile = table.rows[cur.rows[0] as number] ?? [];
      const nameSpalte = mapping.indexOf('teamName');
      const hatInhalt = rohzeile.some((c, ci) => ci !== nameSpalte && (c ?? '').trim() !== '');
      if (cur.team.players.length === 0 && hatInhalt) continue;
      const prev = teams.slice(0, i).find((t) => (t.team.name ?? '').toLowerCase() === key);
      if (!prev || prev.team.players.length + cur.team.players.length > opts.teamSize) continue;
      prev.team.players.push(...cur.team.players);
      prev.rows.push(...cur.rows);
      if (!prev.team.club && cur.team.club) prev.team.club = cur.team.club;
      prev.issues = prev.issues.filter((x) => !/Partner offen|Weder Teamname/.test(x.message));
      if (prev.team.players.length < opts.teamSize)
        prev.issues.push({ row: prev.rows[0] as number, level: 'warning', message: `${prev.team.players.length} von ${opts.teamSize} Spielern — Partner offen` });
      teams.splice(i, 1);
    }
  }

  // Duplikate
  const seen = new Map<string, number>();
  /** Wie oft ein Name schon vorkam — für „(2)", „(3)", „(4)" */
  const wieoft = new Map<string, number>();
  for (const name of opts.existingNames ?? []) seen.set(name.toLowerCase(), -1);
  teams.forEach((t, i) => {
    const key = (t.team.name ?? '').toLowerCase();
    if (!key) return;
    const prev = seen.get(key);
    if (prev !== undefined) {
      t.issues.push({
        row: t.rows[0] as number,
        level: 'warning',
        message: prev === -1 ? `Teamname „${t.team.name}" existiert schon im Turnier` : `Teamname „${t.team.name}" doppelt (auch Zeile ${(teams[prev]?.rows[0] as number) + 1})`,
      });
      // Durchnummerieren statt alle „(2)" zu nennen — sonst stehen drei
      // gleichnamige Zeilen hinterher wieder gleichnamig da
      const n = (wieoft.get(key) ?? 1) + 1;
      wieoft.set(key, n);
      t.team.name = `${t.team.name} (${n})`;
    } else seen.set(key, i);
  });
  const playerSeen = new Map<string, number>();
  teams.forEach((t, i) => {
    for (const p of t.team.players) {
      const key = `${p.firstName} ${p.lastName}`.toLowerCase().trim();
      if (!key) continue;
      const prev = playerSeen.get(key);
      if (prev !== undefined && prev !== i)
        t.issues.push({ row: t.rows[0] as number, level: 'warning', message: `${p.firstName} ${p.lastName} steht auch in Zeile ${(teams[prev]?.rows[0] as number) + 1}` });
      else playerSeen.set(key, i);
    }
  });

  // Setzung normalisieren
  const seeded = teams.filter((t) => t.team.seed !== undefined).sort((a, b) => (a.team.seed as number) - (b.team.seed as number));
  const seedValues = seeded.map((t) => t.team.seed);
  if (new Set(seedValues).size !== seedValues.length || (seeded.length > 0 && seedValues[seeded.length - 1] !== seeded.length)) {
    if (seeded.length > 0) globalIssues.push({ row: -1, level: 'warning', message: 'Setzung lückenhaft oder doppelt — wurde auf 1…n normalisiert' });
    seeded.forEach((t, i) => {
      t.team.seed = i + 1;
    });
  }

  // Limit → Warteliste
  let waitlist = 0;
  if (opts.limit !== undefined) {
    teams.forEach((t, i) => {
      if (i >= (opts.limit as number)) {
        waitlist++;
        t.issues.push({ row: t.rows[0] as number, level: 'warning', message: 'Über dem Teilnehmerlimit → Warteliste' });
        t.team.waitlist = true;
      }
    });
  }

  const withErrors = teams.filter((t) => t.issues.some((i) => i.level === 'error')).length;
  const withWarnings = teams.filter((t) => !t.issues.some((i) => i.level === 'error') && t.issues.length > 0).length;
  return {
    teams,
    issues: globalIssues,
    summary: { total: teams.length, valid: teams.length - withErrors, withWarnings, withErrors, waitlist },
  };
}

function buildTeam(
  rows: number[],
  name: string,
  players: { firstName: string; lastName: string; club?: string }[],
  row: string[],
  cell: (row: string[], role: ColumnRole) => string,
  opts: ImportOptions,
): ImportTeam {
  const issues: ImportIssue[] = [];
  const r0 = rows[0] as number;
  const club = cell(row, 'club') || players.find((p) => p.club)?.club;
  const cleanPlayers = players.map((p) => {
    const out: { firstName: string; lastName: string; club?: string } = { firstName: p.firstName, lastName: p.lastName };
    if (p.club) out.club = p.club;
    return out;
  });
  // Einzelspieler als bloßer Name („harald", „spieler1“): der Name ist der Spieler —
  // sonst hieß jede Zeile „keine Spieler erkannt · 0 von 1 Spielern — Partner offen“ (Harald, 10/2026)
  if (opts.teamSize === 1 && cleanPlayers.length === 0 && name.trim()) {
    const [vor, ...rest] = name.trim().split(/\s+/);
    cleanPlayers.push({ firstName: vor as string, lastName: rest.join(' ') });
  }
  if (cleanPlayers.length === 0 && !name) issues.push({ row: r0, level: 'error', message: 'Weder Teamname noch Spieler' });
  else if (cleanPlayers.length < opts.teamSize) issues.push({ row: r0, level: 'warning', message: `${cleanPlayers.length} von ${opts.teamSize} Spielern — Partner offen` });
  else if (cleanPlayers.length > opts.teamSize) issues.push({ row: r0, level: 'warning', message: `${cleanPlayers.length} Spieler, Bewerb erwartet ${opts.teamSize}` });

  // Einzelspieler heißt wie er selbst — vorher nur der Nachname, und in der Tabelle
  // stand dreimal „Marco" bzw. „Ferrero" ohne Vornamen (Harald, 10/2026)
  const teamName =
    name ||
    (cleanPlayers.length === 1
      ? `${cleanPlayers[0]?.firstName ?? ''} ${cleanPlayers[0]?.lastName ?? ''}`.trim()
      : cleanPlayers
          .map((p) => p.lastName || p.firstName)
          .filter(Boolean)
          .join('/'));
  const team: TeamInput = { players: cleanPlayers, paid: false };
  if (teamName) team.name = titleCase(teamName);
  if (club) team.club = club;
  const seedRaw = cell(row, 'seed');
  if (seedRaw) {
    const n = Number.parseInt(seedRaw, 10);
    if (Number.isFinite(n) && n > 0) team.seed = n;
    else issues.push({ row: r0, level: 'warning', message: `Setzung „${seedRaw}" ist keine Zahl` });
  }
  const paidRaw = cell(row, 'paid');
  if (paidRaw) {
    const b = parseBool(paidRaw);
    if (b === undefined) issues.push({ row: r0, level: 'warning', message: `„${paidRaw}" bei Bezahlt nicht erkannt` });
    else team.paid = b;
  }
  const note = cell(row, 'note');
  if (note) team.note = note;

  const out: ImportTeam = { rows, team, issues };
  const email = cell(row, 'email');
  if (email) {
    if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) out.email = email;
    else issues.push({ row: r0, level: 'warning', message: `E-Mail „${email}" ungültig — leer importiert` });
  }
  const phone = cell(row, 'phone');
  if (phone) out.phone = phone;
  const comp = cell(row, 'competition');
  if (comp) out.competition = comp;
  return out;
}

/** Komplettpfad: Text → Teams (für Schnelleingabe ohne Mapping-Dialog) */
export function importText(text: string, opts: ImportOptions): ImportResult & { table: ParsedTable; mapping: Mapping } {
  const table = parseTable(text);
  const mapping = suggestMapping(table);
  return { ...rowsToTeams(table, mapping, opts), table, mapping };
}
